import * as z from 'zod'
import { blockTypesRecord } from './block-types-record'
import type { BlockType } from '.'
import {
  FilterType,
  SortDirection,
  filtersSchema,
  sortSchema,
  Sort,
} from './search'

export const listViewLayouts = ['table', 'gallery', 'inline'] as const
export const listViewLayoutsEnum = z.enum(listViewLayouts)

// List view filters

export const ALL_FILTER_BLOCK_TYPES = [
  blockTypesRecord.text,
  blockTypesRecord.number,
  blockTypesRecord.date,
  blockTypesRecord.select,
  blockTypesRecord.color,
  blockTypesRecord.reference,
  blockTypesRecord.boolean,
  blockTypesRecord.richText,
  blockTypesRecord.codeSnippet,
  blockTypesRecord.list,
  blockTypesRecord.media,
]

export const LIST_VIEW_FILTER_TYPES: Array<{
  value: FilterType
  label: string
  blocks: BlockType[]
}> = [
  {
    value: 'contains',
    label: 'Contains',
    blocks: [
      blockTypesRecord.text,
      blockTypesRecord.number,
      blockTypesRecord.select,
      blockTypesRecord.color,
      blockTypesRecord.reference,
      blockTypesRecord.richText,
      blockTypesRecord.codeSnippet,
    ],
  },
  {
    value: 'does-not-contain',
    label: 'Does not contain',
    blocks: [
      blockTypesRecord.text,
      blockTypesRecord.number,
      blockTypesRecord.select,
      blockTypesRecord.color,
      blockTypesRecord.reference,
      blockTypesRecord.richText,
      blockTypesRecord.codeSnippet,
    ],
  },
  {
    value: 'is',
    label: 'Is',
    blocks: [
      blockTypesRecord.text,
      blockTypesRecord.number,
      blockTypesRecord.date,
      blockTypesRecord.select,
      blockTypesRecord.color,
      blockTypesRecord.reference,
      blockTypesRecord.richText,
      blockTypesRecord.codeSnippet,
      blockTypesRecord.boolean,
    ],
  },
  {
    value: 'is-not',
    label: 'Is not',
    blocks: [
      blockTypesRecord.text,
      blockTypesRecord.number,
      blockTypesRecord.date,
      blockTypesRecord.color,
    ],
  },
  {
    value: 'starts-with',
    label: 'Starts with',
    blocks: [
      blockTypesRecord.text,
      blockTypesRecord.number,
      blockTypesRecord.select,
      blockTypesRecord.color,
      blockTypesRecord.reference,
      blockTypesRecord.richText,
      blockTypesRecord.codeSnippet,
    ],
  },
  {
    value: 'ends-with',
    label: 'Ends with',
    blocks: [
      blockTypesRecord.text,
      blockTypesRecord.number,
      blockTypesRecord.select,
      blockTypesRecord.color,
      blockTypesRecord.richText,
      blockTypesRecord.reference,
    ],
  },
  {
    value: 'is-empty',
    label: 'Is empty',
    blocks: [
      blockTypesRecord.text,
      blockTypesRecord.number,
      blockTypesRecord.date,
      blockTypesRecord.select,
      blockTypesRecord.color,
      blockTypesRecord.reference,
      blockTypesRecord.richText,
      blockTypesRecord.codeSnippet,
      blockTypesRecord.list,
      blockTypesRecord.media,
    ],
  },
  {
    value: 'is-not-empty',
    label: 'Is not empty',
    blocks: [
      blockTypesRecord.text,
      blockTypesRecord.number,
      blockTypesRecord.date,
      blockTypesRecord.select,
      blockTypesRecord.color,
      blockTypesRecord.reference,
      blockTypesRecord.richText,
      blockTypesRecord.codeSnippet,
      blockTypesRecord.list,
      blockTypesRecord.media,
    ],
  },
  {
    value: 'greater-than',
    label: 'Greater than',
    blocks: [
      blockTypesRecord.number,
      blockTypesRecord.date,
      blockTypesRecord.list,
    ],
  },
  {
    value: 'lower-than',
    label: 'Lower than',
    blocks: [
      blockTypesRecord.number,
      blockTypesRecord.date,
      blockTypesRecord.list,
    ],
  },
]

// List view sort
export const sortDirectionsRecord: Record<SortDirection, string> = {
  asc: 'Ascending',
  desc: 'Descending',
}

export const ALL_SORT_BLOCK_TYPES = [
  blockTypesRecord.text,
  blockTypesRecord.number,
  blockTypesRecord.date,
  blockTypesRecord.boolean,
  blockTypesRecord.color,
  blockTypesRecord.select,
  blockTypesRecord.codeSnippet,
  blockTypesRecord.icon,
  // blockTypesRecord.media,
  // blockTypesRecord.richText,
  // blockTypesRecord.event,
]

export type ListSort = Sort

export const tableViewSchema = z.object({
  layout: z.literal(listViewLayoutsEnum.Enum.table),

  id: z.string(),
  title: z.string(),
  filter: filtersSchema,
  sort: z.array(sortSchema),
  showOnFirstLoad: z.union([
    z.literal(10),
    z.literal(25),
    z.literal(50),
    z.literal(100),
  ]),
  columnsMetadata: z.array(
    z.object({
      blockId: z.string(),
      width: z.number(),
      hidden: z.boolean(),
    })
  ),
})

export type CollectionView = z.infer<typeof tableViewSchema>

export const galleryViewSchema = z.object({
  layout: z.literal(listViewLayoutsEnum.Enum.gallery),

  id: z.string(),
  title: z.string(),
  filter: filtersSchema,
  sort: z.array(sortSchema),
  showOnFirstLoad: z.union([
    z.literal(10),
    z.literal(25),
    z.literal(50),
    z.literal(100),
  ]),
  columnsMetadata: z.array(
    z.object({
      blockId: z.string(),
      width: z.number(),
      hidden: z.boolean(),
    })
  ),

  thumbnailColumn: z.string().optional(),
})

export type GalleryView = z.infer<typeof galleryViewSchema>

const inlineViewSchema = z.object({
  layout: z.literal(listViewLayoutsEnum.Enum.inline),

  id: z.string(),
  title: z.string(),
  filter: filtersSchema,
  sort: z.array(sortSchema),
  showOnFirstLoad: z.union([
    z.literal(10),
    z.literal(25),
    z.literal(50),
    z.literal(100),
  ]),
  columnsMetadata: z.array(
    z.object({
      blockId: z.string(),
      width: z.number(),
      hidden: z.boolean(),
    })
  ),

  thumbnailColumn: z.string().optional(),
})

export type InlineView = z.infer<typeof inlineViewSchema>

export const listViewSchema = z.discriminatedUnion('layout', [
  tableViewSchema,
  galleryViewSchema,
  inlineViewSchema,
])

export type ListView = z.infer<typeof listViewSchema>

export const DEFAULT_LIST_VIEW: ListView = {
  title: '',
  filter: [],
  sort: [],
  showOnFirstLoad: 10,
  columnsMetadata: [],
  id: '',
  layout: 'table',
}
